import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { ContentType } from '@/api/generated-api'
import { apiRequest } from '@/shared/api/client'

import type {
  ClubFinance,
  ClubWithdrawal,
  FanIncomePage
} from '../lib/clubMonetization'

// The owner's Monetization tab — GET/POST /v2/clubs/me/{finance,withdrawals,
// fan-income} (not in the generated client yet).

const FINANCE_KEY = 'club_finance'
const WITHDRAWALS_KEY = 'club_withdrawals'
const FAN_INCOME_KEY = 'club_fan_income'

// shortcut: one page of the newest requests, no paging — upgrade when clubs
// pile up more than WITHDRAWALS_LIMIT requests.
const WITHDRAWALS_LIMIT = 50
const FAN_INCOME_PAGE = 10

export function useClubFinance() {
  return useQuery({
    queryKey: [FINANCE_KEY],
    queryFn: () =>
      apiRequest<ClubFinance>({
        path: '/api/v2/clubs/me/finance',
        method: 'GET',
        format: 'json'
      }),
    staleTime: 30_000
  })
}

export function useClubWithdrawals() {
  return useQuery({
    queryKey: [WITHDRAWALS_KEY],
    queryFn: async () => {
      const response = await apiRequest<{ withdrawals?: ClubWithdrawal[] }>({
        path: '/api/v2/clubs/me/withdrawals',
        method: 'GET',
        query: { limit: WITHDRAWALS_LIMIT },
        format: 'json'
      })
      return response?.withdrawals ?? []
    },
    staleTime: 30_000
  })
}

export function useCreateWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (amountAp: number) =>
      apiRequest<ClubWithdrawal>({
        path: '/api/v2/clubs/me/withdrawals',
        method: 'POST',
        body: { amount_ap: amountAp },
        type: ContentType.Json,
        format: 'json'
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [FINANCE_KEY] }),
        queryClient.invalidateQueries({ queryKey: [WITHDRAWALS_KEY] })
      ])
  })
}

export function useFanIncome(filters: { platform: string; country: string }) {
  return useInfiniteQuery({
    queryKey: [FAN_INCOME_KEY, filters.platform, filters.country],
    queryFn: ({ pageParam }) =>
      apiRequest<FanIncomePage>({
        path: '/api/v2/clubs/me/fan-income',
        method: 'GET',
        query: {
          limit: FAN_INCOME_PAGE,
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(filters.platform ? { platform: filters.platform } : {}),
          ...(filters.country ? { country: filters.country } : {})
        },
        format: 'json'
      }),
    initialPageParam: '',
    getNextPageParam: (last: FanIncomePage) => last?.next_cursor || undefined,
    staleTime: 30_000
  })
}

export function useFanIncomeCountries() {
  return useQuery({
    queryKey: [FAN_INCOME_KEY, 'countries'],
    queryFn: async () => {
      const response = await apiRequest<{
        countries?: Array<{ iso2: string; name: string; count: number }>
      }>({
        path: '/api/v2/clubs/me/fan-income/countries',
        method: 'GET',
        format: 'json'
      })
      return response?.countries ?? []
    },
    staleTime: 60_000
  })
}
